import axios, { AxiosError } from 'axios'
import type { AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth.store'
import type { ApiError } from '@/types'

const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

function hasMessage(data: unknown): data is { message: string } {
  return typeof data === 'object' && data !== null && 'message' in data
}

function extractApiError(error: AxiosError): ApiError {
  const data = error.response?.data
  return {
    message: hasMessage(data) ? data.message : error.message || 'Unknown error',
    statusCode: error.response?.status,
    data: error.response?.data,
  }
}

async function request<T>(promise: Promise<AxiosResponse<T>>): Promise<T> {
  try {
    const response = await promise
    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw extractApiError(error)
    } else {
      throw {
        message: 'Unexpected error occurred',
      } as ApiError
    }
  }
}

export default {
  api,
  get: <T>(url: string, config = {}) => request<T>(api.get(url, config)),
  post: <T>(url: string, data?: unknown, config = {}) => request<T>(api.post(url, data, config)),
  // Add put, delete, etc. as needed
}

api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const token = authStore.accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const authStore = useAuthStore()

    // We've been here before
    if (originalRequest.url?.includes('/auth/login')) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      try {
        await authStore.refreshToken()
        originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`

        return api(originalRequest)
      } catch {
        authStore.logout()

        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)
